// ============================================
// Waitlist + Invite codes (landing page)
// ============================================

import { supabase } from '../../lib/supabase';

// ---- Waitlist (landing page email capture) ----
// `source` attributes the signup to a page / magnet / partner QR
// (G2M plan finding F6). Column added in 20260712_waitlist_source.sql;
// if that migration hasn't been applied yet the insert retries without
// the column so capture never breaks on deploy-order drift.
export async function submitWaitlistEmail(email: string, source?: string): Promise<{ success: boolean; duplicate?: boolean; error?: string }> {
  const row: { email: string; source?: string } = { email };
  if (source) row.source = source.slice(0, 128);

  let { error } = await supabase.from('waitlist').insert(row);

  if (error && source && error.code !== '23505' && /source/i.test(error.message || '')) {
    // Column not there yet - capture the email anyway.
    ({ error } = await supabase.from('waitlist').insert({ email }));
  }

  if (error) {
    if (error.code === '23505') {
      // Unique constraint violation - already signed up. Success for the UI,
      // but flagged so callers don't count it as a fresh conversion.
      return { success: true, duplicate: true };
    }
    return { success: false, error: 'Something went wrong. Please try again.' };
  }

  // Fresh (non-duplicate) capture: fire-and-forget the welcome email with the
  // matching lead magnet. Never awaited and never allowed to throw, so a send
  // failure (or a not-yet-configured RESEND_API_KEY) can't slow or break the
  // form UX. Idempotency is enforced server-side (atomic claim on the waitlist
  // row), so a retry or double-fire never double-sends.
  try {
    void supabase.functions
      .invoke('send-welcome-email', { body: { type: 'waitlist', email, source } })
      .catch(() => { /* fire-and-forget: send is best-effort */ });
  } catch { /* invoke threw synchronously (offline): ignore */ }

  return { success: true };
}

// ---- Invite Code Validation ----
export async function validateInviteCode(code: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('validate_invite_code', {
    input_code: code.trim().toUpperCase(),
  });
  if (error) {
    console.error('Invite code validation error:', error);
    return false;
  }
  return data === true;
}
